import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { theme, darkTheme } from './tokens';

// Crear contexto del tema con soporte de modo
const ThemeContext = createContext({ theme, mode: 'light', toggleMode: () => {} });

// Provider del tema
export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => localStorage.getItem('gaero-ui-mode') || 'light');

  useEffect(() => {
    // Persistir y setear atributo para CSS
    localStorage.setItem('gaero-ui-mode', mode);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', mode);
    }
  }, [mode]);

  // Seleccionar el tema apropiado basado en el modo
  const currentTheme = mode === 'dark' ? darkTheme : theme;

  const value = useMemo(() => ({ 
    theme: currentTheme, 
    mode, 
    toggleMode: () => setMode((m) => (m === 'light' ? 'dark' : 'light')) 
  }), [mode, currentTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Hook para usar el tema
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme debe ser usado dentro de un ThemeProvider');
  }
  return context;
};

// Hook para usar tokens específicos
export const useThemeTokens = () => {
  const { theme } = useTheme();
  
  // Funciones helper para acceder fácilmente a los tokens
  const getColor = (path) => {
    const keys = path.split('.');
    let value = theme.colors;
    for (const key of keys) {
      value = value[key];
      if (!value) return undefined;
    }
    return value;
  };
  
  const getSpacing = (size) => {
    return theme.spacing[size] || size;
  };
  
  const getFontSize = (size) => {
    return theme.typography.fontSizes[size] || size;
  };
  
  const getFontWeight = (weight) => {
    return theme.typography.fontWeights[weight] || weight;
  };
  
  const getBorderRadius = (size) => {
    return theme.borderRadius[size] || size;
  };
  
  const getShadow = (level) => {
    return theme.shadows[level] || level;
  };
  
  const getComponent = (componentName) => {
    return theme.components[componentName];
  };
  
  return {
    theme,
    getColor,
    getSpacing,
    getFontSize,
    getFontWeight,
    getBorderRadius,
    getShadow,
    getComponent,
    // Acceso directo a categorías principales
    colors: theme.colors,
    typography: theme.typography,
    spacing: theme.spacing,
    borderRadius: theme.borderRadius,
    shadows: theme.shadows,
    layout: theme.layout,
    animations: theme.animations,
    components: theme.components,
    // Acceso a configuraciones específicas de modo oscuro si están disponibles
    darkMode: theme.darkMode,
  };
};

export default ThemeProvider;
